import type { Client } from '@optimizely/optimizely-sdk';
import { createInstance, enums } from '@optimizely/optimizely-sdk';

// eslint-disable-next-line import/no-restricted-paths -- server-only module
import getServerConfig from 'Config/serverConfig';
import { logger } from 'Services/logger/logger';
import type { ConfigDatafile } from 'Services/optimizely/optimizely';
import { convertToError } from 'Utilities/error/convertToError';

const SCOPE = 'serverOptimizely';

const READY_TIMEOUT_MS = 5_000;
const DEFAULT_DECISION_READY_TIMEOUT_MS = 2_000;
const DATAFILE_UPDATE_INTERVAL_MS = 60_000;

const getDecisionReadyTimeoutMs = (): number => {
	const raw: unknown = getServerConfig('optimizely_decision_ready_timeout_ms');
	const timeout = typeof raw === 'string' && raw !== '' ? Number(raw) : raw;

	if (typeof timeout === 'number' && Number.isFinite(timeout) && timeout >= 0) {
		return timeout;
	}

	return DEFAULT_DECISION_READY_TIMEOUT_MS;
};

let client: Client | null = null;
let initialisationPromise: Promise<Client | null> | null = null;
let isClientReady = false;
let readyPromise: Promise<void> | null = null;

/**
 * Lazily create and return the server-side Optimizely client singleton.
 *
 * Pass a `datafile` to make the client usable immediately — the SDK can
 * serve decisions from the datafile without waiting for a CDN download.
 * The SDK will still poll for datafile updates in the background.
 *
 * Without a datafile the SDK must download one first; callers should use
 * `onReady` or the decision-time readiness wait before trusting results.
 */
export const getServerOptimizelyClient = (datafile?: ConfigDatafile): Promise<Client | null> => {
	if (client) return Promise.resolve(client);
	if (initialisationPromise) return initialisationPromise;

	initialisationPromise = (async () => {
		const sdkKey = getServerConfig('optimizely_sdk_key');

		if (!sdkKey) {
			logger.warn({ message: 'Optimizely SDK key not configured — server-side feature flags disabled', scope: SCOPE });
			return null;
		}

		const optimizelyUrl: string | undefined = getServerConfig('optimizely_url') || undefined;

		const instance = createInstance({
			...(datafile ? { datafile } : {}),
			datafileOptions: {
				autoUpdate: true,
				updateInterval: DATAFILE_UPDATE_INTERVAL_MS,
				...(optimizelyUrl && { urlTemplate: `${optimizelyUrl}/datafiles/%s.json` }),
			},
			logLevel: enums.LOG_LEVEL.ERROR,
			odpOptions: { disabled: true },
			sdkKey,
		});

		if (!instance) {
			throw new Error('Failed to create Optimizely client instance');
		}

		client = instance;

		// When a datafile is provided the SDK can make decisions immediately —
		// there's no need to wait for onReady (which waits for the CDN
		// download, not the datafile to be loaded).  We mark the client as
		// ready straight away so decision calls are never blocked.
		//
		// Without a datafile the SDK must download one first, so we store the
		// onReady promise and let callers race against a timeout at decision
		// time.
		if (datafile) {
			isClientReady = true;
		}

		// Timed readiness check — used for observability logging only.
		instance
			.onReady({ timeout: READY_TIMEOUT_MS })
			.then(({ reason, success }) => {
				if (!success && !datafile) {
					logger.warn({
						message: `Optimizely client not ready within ${READY_TIMEOUT_MS}ms: ${reason ?? 'unknown'}`,
						scope: SCOPE,
					});
				}
			})
			.catch((err: unknown) => {
				logger.error({ error: convertToError(err), message: 'Optimizely client readiness check failed', scope: SCOPE });
			});

		// Long-lived readiness listener — no timeout.  If the timed check
		// above resolves with success=false (e.g. slow CDN), this ensures
		// isClientReady is still flipped once the SDK eventually becomes ready.
		// Guard against stale callbacks after _resetForTesting() by checking
		// that the resolved promise still corresponds to the current client.
		const longLivedReady = instance
			.onReady()
			.then(({ success }) => {
				if (success && client === instance) {
					isClientReady = true;
				}
			})
			.catch((err: unknown) => {
				logger.error({
					error: convertToError(err),
					message: 'Optimizely long-lived readiness check failed',
					scope: SCOPE,
				});
			});

		if (!datafile) {
			readyPromise = longLivedReady;
		}

		return client;
	})().catch((err) => {
		logger.error({ error: convertToError(err), message: 'Optimizely client initialisation failed', scope: SCOPE });
		initialisationPromise = null;
		return null;
	});

	return initialisationPromise;
};

/**
 * Make a server-side Optimizely feature flag decision.
 *
 * Returns `true`/`false` for real decisions, or `null` when no reliable
 * decision could be made (client unavailable, datafile not yet loaded, etc.).
 *
 * When the client was pre-warmed with a datafile (the normal startup path),
 * `isClientReady` is already `true` and decisions are served immediately.
 * The readiness wait only applies to the lazy-init fallback (no datafile),
 * where the SDK must download the datafile before it can make decisions.
 */
export const getServerOptimizelyDecision = async (
	flagKey: string,
	userId: string,
	attributes?: Record<string, string | boolean | number | null>,
): Promise<{ enabled: boolean | null; variationKey: string | null }> => {
	try {
		const instance = await getServerOptimizelyClient();

		if (!instance) return { enabled: null, variationKey: null };

		if (!isClientReady && readyPromise) {
			const decisionTimeout = getDecisionReadyTimeoutMs();
			let timeoutId: ReturnType<typeof setTimeout> | null = null;

			type ReadyResult = { ready: boolean; reason: 'onReady' } | { ready: false; reason: 'timeout' };

			const timeoutPromise = new Promise<ReadyResult>((resolve) => {
				timeoutId = setTimeout(() => resolve({ ready: false, reason: 'timeout' }), decisionTimeout);
			});

			const onReadyResult: Promise<ReadyResult> = readyPromise
				.then((): ReadyResult => ({ ready: isClientReady, reason: 'onReady' }))
				.finally(() => {
					if (timeoutId !== null) clearTimeout(timeoutId);
				});

			const readyResult = await Promise.race([onReadyResult, timeoutPromise]);

			if (!readyResult.ready) {
				const message =
					readyResult.reason === 'timeout'
						? `Optimizely not ready within ${decisionTimeout}ms — skipping decision for flag=${flagKey}`
						: `Optimizely client failed to become ready — skipping decision for flag=${flagKey}`;

				logger.info({ message, scope: SCOPE });
				return { enabled: null, variationKey: null };
			}
		}

		const userContext = instance.createUserContext(userId, attributes);

		if (!userContext) {
			logger.warn({ message: `Failed to create user context for userId=${userId}`, scope: SCOPE });
			return { enabled: null, variationKey: null };
		}

		const decision = userContext.decide(flagKey);

		logger.debug({
			context: {
				...(attributes && { attributes }),
				enabled: decision.enabled,
				flagKey,
				userId,
				variationKey: decision.variationKey,
			},
			message: `Flag decision: ${flagKey}=${decision.enabled}`,
			scope: SCOPE,
		});

		return { enabled: decision.enabled, variationKey: decision.variationKey };
	} catch (error) {
		logger.error({
			error: convertToError(error),
			message: `Optimizely decision failed for flag=${flagKey}, userId=${userId}`,
			scope: SCOPE,
		});
		return { enabled: null, variationKey: null };
	}
};

/** Reset the singleton — only for use in tests. @internal */
// eslint-disable-next-line no-underscore-dangle
export const _resetForTesting = (): void => {
	if (client) {
		client.close().catch(() => {
			// Swallow errors during test teardown
		});
	}
	client = null;
	initialisationPromise = null;
	isClientReady = false;
	readyPromise = null;
};
